/*
 * Useful to generate consistent routes
 */

type NumberOrString = number | string;

/**
 * @param pageId number | string
 * @param itemId number | string
 * @returns string
 */
type ItemRouteArgs = (pageId: NumberOrString, itemId: NumberOrString, preview?: boolean) => string;

export const dashboardRoute = '/gs/dashboard';
export const previewRoute = `${dashboardRoute}/preview`;

export const pageRoute = (pageId: NumberOrString, preview: boolean): string =>
  `${preview ? previewRoute : dashboardRoute}/pages/${pageId}`;

const itemRoute = (pageId: NumberOrString, itemType: string, itemId: NumberOrString, preview: boolean): string =>
  `${pageRoute(pageId, preview)}/${itemType}/${itemId}`;

export const pageItemTypeRoute = (
  pageType: string,
  pageId: NumberOrString,
  itemId: NumberOrString,
  preview = false
): string => {
  switch (pageType) {
    case 'HeadlineItem':
      return headlineItemRoute(pageId, itemId, preview);
    case 'ParagraphItem':
      return paragraphItemRoute(pageId, itemId, preview);
    case 'ActivityItem':
      return activityItemRoute(pageId, itemId, preview);
    case 'AccommodationItem':
      return accommodationItemRoute(pageId, itemId, preview);
    case 'TransportationItem':
      return transportationItemRoute(pageId, itemId, preview);
    case 'PhotoGalleryItem':
      return photoGalleryItemRoute(pageId, itemId, preview);
    case 'PhotoTimelineItem':
      return photoTimelineItemRoute(pageId, itemId, preview);
    case 'StoryItem':
      return storyItemRoute(pageId, itemId, preview);
    case 'DressCodeItem':
      return dressCodeItemRoute(pageId, itemId, preview);
    case 'PhotoItem':
      return photoItemRoute(pageId, itemId, preview);
    case 'GifItem':
      return photoItemRoute(pageId, itemId, preview);
    case 'EventGroupItem':
      return pageRoute(pageId, preview);
    case 'PersonItem':
      return personItemRoute(pageId, itemId, preview);
    case 'QuestionItem':
      return questionItemRoute(pageId, itemId, preview);
    case 'LivestreamItem':
      return livestreamItemRoute(pageId, itemId, preview);
    default:
      return previewRoute;
  }
};

export const headlineItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'headline-items', itemId, preview);

export const paragraphItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'paragraph-items', itemId, preview);

export const activityItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'activity-items', itemId, preview);

export const accommodationItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'accommodation-items', itemId, preview);

export const transportationItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'transportation-items', itemId, preview);

export const photoGalleryItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'photo-gallery-items', itemId, preview);

export const photoTimelineItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'photo-timeline-items', itemId, preview);

export const storyItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'story-items', itemId, preview);

export const newItemsRoute = (pageId: NumberOrString, preview = false): string =>
  `${pageRoute(pageId, preview)}/page-items/new`;

export const basicItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'basic-items', itemId, preview);

export const personItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'person-items', itemId, preview);

export const photoItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'photo-items', itemId, preview);

export const embedItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'embed-items', itemId, preview);

export const questionItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'question-items', itemId, preview);

export const dressCodeItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'dress-code-items', itemId, preview);

export const livestreamItemRoute: ItemRouteArgs = (pageId, itemId, preview = false) =>
  itemRoute(pageId, 'livestream-items', itemId, preview);

const managePagesRoute = `${dashboardRoute}/manage-pages`;
export const reorderPagesRoute = `${managePagesRoute}/reorder-pages`;
export const addPageRoute = `${managePagesRoute}/add-page`;

export const addPagePreviewRoute = `${dashboardRoute}/preview/add-page`;

export const pageSettingsRoute = (pageId: NumberOrString, preview = false): string =>
  `${pageRoute(pageId, preview)}/page-settings`;

export const websiteSettingsRoute = `${dashboardRoute}/website-settings`;

export const basicInfoPath = `/basic-info`;

export const basicInfoRailRoute = (preview: boolean) => `${preview ? previewRoute : dashboardRoute}${basicInfoPath}`;

export const previewPageRoute = (location: string) => `${previewRoute}?location=${location}`;

export const previewPageRouteWithOpenEditRail = (location: string) =>
  `${previewRoute}?location=${location}&open_edit=true`;

export const changeThemeRoute = `${previewRoute}?open=true`;

export const editorDesignsRoute = `${previewRoute}/designs/browse`;

export const previewThemeRoute = (themeId: NumberOrString): string => `${previewRoute}/${themeId}`;

export const previewThemeRouteMatch = `${previewRoute}/:themeId`;

export const purchaseDomainRoute = `${dashboardRoute}/purchase-domain`;

export const purchaseDomainRenewalRoute = `${purchaseDomainRoute}/renew`;

// messaging routes
export const messagingHubRoute = `${dashboardRoute}/messaging-hub`;
export const sentMessagesRoute = `${messagingHubRoute}/sent-messages`;

// saveTheDate Sequence
export const saveTheDateEmailRoute = `${messagingHubRoute}/save-the-date-email/:seqId`;
export const editSaveTheDateEmailPath = `${saveTheDateEmailRoute}/edit`;
export const editSaveTheDateEmailRoute = (seqId: string) => `${saveTheDateEmailRoute.replace(':seqId', seqId)}/edit`;

// ShareWebsite Sequence
export const shareWebsiteEmailRoute = `${messagingHubRoute}/share-website-email/:seqId`;
export const shareWebsiteSMSRoute = `${messagingHubRoute}/share-website-sms/:seqId`;

// SendUpdates Sequence
export const sendUpdatesEmailRoute = `${messagingHubRoute}/send-updates-email/:seqId`;
export const sendUpdatesSMSRoute = `${messagingHubRoute}/send-updates-sms/:seqId`;

// Share Day Of Sequence
export const shareDayOfDetailsEmailRoute = `${messagingHubRoute}/share-day-of-details-email/:seqId`;
export const shareDayOfDetailsSMSRoute = `${messagingHubRoute}/share-day-of-details-sms/:seqId`;

// Share Website Edit
export const editShareWebsiteEmailPath = `${shareWebsiteEmailRoute}/edit`;
export const editShareWebsiteEmailRoute = (seqId: string) => `${shareWebsiteEmailRoute.replace(':seqId', seqId)}/edit`;

export const editShareWebsiteSmsPath = `${shareWebsiteSMSRoute}/edit`;
export const editShareWebsiteSmsRoute = (seqId: string) => `${shareWebsiteSMSRoute.replace(':seqId', seqId)}/edit`;

// Share day of  Edit
export const editShareDayOfDetailsEmailPath = `${shareDayOfDetailsEmailRoute}/edit`;
export const editShareDayOfDetailsEmailRoute = (seqId: string) =>
  `${shareDayOfDetailsEmailRoute.replace(':seqId', seqId)}/edit`;

export const editShareDayOfDetailsSmsPath = `${shareDayOfDetailsSMSRoute}/edit`;
export const editShareDayOfDetailsSmsRoute = (seqId: string) =>
  `${shareDayOfDetailsSMSRoute.replace(':seqId', seqId)}/edit`;
// Send Updates Edit
export const editSendUpdatesEmailPath = `${sendUpdatesEmailRoute}/edit`;
export const editSendUpdatesEmailRoute = (seqId: string) => `${sendUpdatesEmailRoute.replace(':seqId', seqId)}/edit`;

export const editSendUpdatesSmsPath = `${sendUpdatesSMSRoute}/edit`;
export const editSendUpdatesSmsRoute = (seqId: string) => `${sendUpdatesSMSRoute.replace(':seqId', seqId)}/edit`;

// Covid Sequence
export const covidEmailRoute = `${messagingHubRoute}/covid-announcement-email/:seqId`;
export const covidSMSRoute = `${messagingHubRoute}/covid-announcement-sms/:seqId`;

// Covid Edit
export const editCovidEmailPath = `${covidEmailRoute}/edit`;
export const editCovidEmailRoute = (seqId: string) => `${covidEmailRoute.replace(':seqId', seqId)}/edit`;

export const editCovidSmsPath = `${covidSMSRoute}/edit`;
export const editCovidSmsRoute = (seqId: string) => `${covidSMSRoute.replace(':seqId', seqId)}/edit`;

// Custom Sequence
export const customEmailRoute = `${messagingHubRoute}/custom-message-email/:seqId`;

// Custom Edit
export const editCustomEmailPath = `${customEmailRoute}/edit`;
export const editCustomEmailRoute = (seqId: string) => `${customEmailRoute.replace(':seqId', seqId)}/edit`;

// Address Collection Sequence
export const addressCollectionEmailRoute = `${messagingHubRoute}/collect-addresses-email/:seqId`;
export const addressCollectionSMSRoute = `${messagingHubRoute}/collect-addresses-sms/:seqId`;

// Address Collection Edit
export const editAddressCollectionPath = `${addressCollectionEmailRoute}/edit`;
export const editAddressCollectionEmailRoute = (seqId: string) =>
  `${addressCollectionEmailRoute.replace(':seqId', seqId)}/edit`;

export const editAddressCollectionSmsPath = `${addressCollectionSMSRoute}/edit`;
export const editAddressCollectionSmsRoute = (seqId: string) =>
  `${addressCollectionSMSRoute.replace(':seqId', seqId)}/edit`;

// Triggered RSVP reminder Sequence
export const rsvpDeadlineReminderEmailRoute = `${messagingHubRoute}/rsvp-reminder-email/:seqId`;
export const rsvpDeadlineReminderSmsRoute = `${messagingHubRoute}/rsvp-reminder-sms/:seqId`;

// Triggered RSVP reminder Edit
export const editRsvpDeadlineReminderEmailPath = `${rsvpDeadlineReminderEmailRoute}/edit`;
export const editRsvpDeadlineReminderEmailRoute = (seqId: string) =>
  `${rsvpDeadlineReminderEmailRoute.replace(':seqId', seqId)}/edit`;

export const editRsvpDeadlineReminderSmsPath = `${rsvpDeadlineReminderSmsRoute}/edit`;
export const editRsvpDeadlineReminderSmsRoute = (seqId: string) =>
  `${rsvpDeadlineReminderSmsRoute.replace(':seqId', seqId)}/edit`;

// Livestream Sequence
export const livestreamEmailRoute = `${messagingHubRoute}/livestream-info-email/:seqId`;

// Livestream Edit
export const editLivestreamEmailPath = `${livestreamEmailRoute}/edit`;
export const editLivestreamEmailRoute = (seqId: string) => `${livestreamEmailRoute.replace(':seqId', seqId)}/edit`;

// Share Local Hotel Sequence
export const shareLocalHotelEmailRoute = `${messagingHubRoute}/share-local-hotel-email/:seqId`;
export const shareLocalHotelSMSRoute = `${messagingHubRoute}/share-local-hotel-sms/:seqId`;
export const editShareLocalHotelEmailPath = `${shareLocalHotelEmailRoute}/edit`;
export const editShareLocalHotelEmailRoute = (seqId: string) =>
  `${shareLocalHotelEmailRoute.replace(':seqId', seqId)}/edit`;
export const editShareLocalHotelSmsPath = `${shareLocalHotelSMSRoute}/edit`;
export const editShareLocalHotelSmsRoute = (seqId: string) =>
  `${shareLocalHotelSMSRoute.replace(':seqId', seqId)}/edit`;

// RSVP Workflow
export const rsvpWorkflowRoute = `${dashboardRoute}/rsvp-workflow`;
export const rsvpEventsRoute = `${rsvpWorkflowRoute}/events`;
export const rsvpGeneralQuestionsRoute = `${rsvpWorkflowRoute}/generalQuestions`;
export const rsvpAccessRoute = `${rsvpWorkflowRoute}/access`;
export const rsvpSuccessRoute = `${rsvpWorkflowRoute}/success`;

// rsvp settings rail
export const manageRsvpSettingsRoute = (preview?: boolean) =>
  `${preview ? previewRoute : dashboardRoute}/manage-rsvp-settings`;

// Registry routes
export const addToRegistryRoute = '/registry/manage/add';
export const createCashFundRoute = '/registry/cash#cash-onboarding-create';

// vendor venues
export const weddingVenuesRoute = '/marketplace/wedding-reception-venues';

// paper
export const paperDraftsRoute = '/paper/your-drafts';
export const paperCartRoute = '/paper/cart';

// RSVP Settings
export const rsvpSettingsRoute = `${dashboardRoute}/rsvp-settings`;

// GLM - Guest List Manager
export const guestListRoute = `/our-guest-list`;
export const guestListEventRoute = (eventId: string) => `/our-guest-list?event=${eventId}`;
export const addressCollectionRoute = `/address-collection`;
export const importSpreadSheetRoute = `/import-spreadsheet`;

// ! routes not in use
export const weddingDateSettingsRoute = '/wedding-date-settings';
export const eventModalRoute = (eventId: string): string => `/event/${eventId}`;

// WWS Onboarding
export const onboardingDesignPLPRoute = '/gs/wedding-websites/designs';
export const onboardingDesignPDPRoute = (themeId: NumberOrString): string => `${onboardingDesignPLPRoute}/${themeId}`;

export const pageItemTypeByRoute = (pageTypeRoute: string): string => {
  switch (pageTypeRoute) {
    case 'headline-items':
      return 'HeadlineItem';
    case 'paragraph-items':
      return 'ParagraphItem';
    case 'activity-items':
      return 'ActivityItem';
    case 'accommodation-items':
      return 'AccommodationItem';
    case 'transportation-items':
      return 'TransportationItem';
    case 'photo-gallery-items':
      return 'PhotoGalleryItem';
    case 'photo-timeline-items':
      return 'PhotoTimelineItem';
    case 'story-items':
      return 'StoryItem';
    case 'dress-code-items':
      return 'DressCodeItem';
    case 'photo-items':
      return 'PhotoItem';
    case 'gif-items':
      return 'GifItem';
    case 'event-group-items':
      return 'EventGroupItem';
    case 'person-items':
      return 'PersonItem';
    case 'question-items':
      return 'QuestionItem';
    case 'livestream-items':
      return 'LivestreamItem';
    default:
      return '';
  }
};
